import { useLayoutEffect } from "react"
import { OK } from "helpers/data-statuses"
import type { IsoDate } from "helpers/types/dates"
import type {
  OptimizationExperimentSource,
  OptimizationSourceKind,
} from "connected-components/optimize-experiments/helpers/get-optimization-position-set"
import { usePortfolioExperiments } from "./use-portfolio-experiments"

export function shouldClearStaleOptimizationExperimentSelection(
  experiments: readonly { id: string, availableFrom?: string }[],
  endDate: IsoDate | null | undefined,
  optimizationSourceKind: OptimizationSourceKind,
  optimizationExperimentSource: OptimizationExperimentSource | null,
): boolean {
  if (optimizationSourceKind !== "experiment" || !optimizationExperimentSource) {
    return false
  }
  const meta = experiments.find(e => e.id === optimizationExperimentSource.experimentId)
  // A missing row is often a stale portfolioExperiments cache (no refetch while result.id matches)
  // or a newly created experiment not merged into loader state — not proof the experiment vanished.
  if (!meta) {
    return false
  }
  if (!endDate) {
    return false
  }
  if (!meta.availableFrom) {
    return true
  }
  if (meta.availableFrom > endDate) {
    return true
  }
  return false
}

interface InvalidateStaleOptimizationExperimentArgs {
  portfolioId: string | undefined
  endDate: IsoDate | null | undefined
  optimizationSourceKind: OptimizationSourceKind
  optimizationExperimentSource: OptimizationExperimentSource | null
  setOptimizationSourceKind: (kind: OptimizationSourceKind) => void
}

/** Resets persisted "optimize from experiment" prefs when the experiment no longer exists or is not valid for the analysis date. */
export function useInvalidateStaleOptimizationExperiment({
  portfolioId,
  endDate,
  optimizationSourceKind,
  optimizationExperimentSource,
  setOptimizationSourceKind,
}: InvalidateStaleOptimizationExperimentArgs): void {
  const { experiments, status } = usePortfolioExperiments(portfolioId)

  useLayoutEffect(() => {
    if (!portfolioId || status !== OK) {
      return
    }
    const shouldClear = shouldClearStaleOptimizationExperimentSelection(
      experiments,
      endDate,
      optimizationSourceKind,
      optimizationExperimentSource,
    )
    if (shouldClear) {
      setOptimizationSourceKind("original")
    }
  }, [
    portfolioId,
    status,
    experiments,
    endDate,
    optimizationSourceKind,
    optimizationExperimentSource,
    setOptimizationSourceKind,
  ])
}
